import { useState } from "react";
import { Target, BarChart3, TrendingUp, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { ProgressBar } from "@/components/shared/progress-bar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { YEAR_OPTIONS, DEFAULT_YEAR } from "@/lib/year-options";
import { StatCard } from "@/features/dashboard/stat-card";
import { QuarterCompletionChart } from "@/features/dashboard/quarter-completion-chart";
import { AtRiskList } from "@/features/dashboard/at-risk-list";
import { useEmployeeDashboard } from "@/hooks/use-dashboard";

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-48" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center gap-4 p-6">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-7 w-12" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-44" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

export function DashboardPage() {
  const [year, setYear] = useState(DEFAULT_YEAR);

  const { data, isLoading, error } = useEmployeeDashboard(year);
  const summary = data?.data;

  if (isLoading) return <DashboardSkeleton />;

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-semibold">Dashboard</h1>
        <EmptyState
          title="Failed to load dashboard"
          description={error.message}
        />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="space-y-6">
        <h1 className="font-heading text-3xl font-semibold">Dashboard</h1>
        <EmptyState
          title="No data available"
          description="Create objectives and key results to see your dashboard."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-semibold sm:text-3xl">Dashboard</h1>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-full sm:w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YEAR_OPTIONS.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Objectives"
          value={summary.total_objectives}
          icon={<Target className="h-6 w-6 text-[hsl(var(--primary))]" />}
        />
        <StatCard
          title="Key Results"
          value={summary.total_key_results}
          icon={<BarChart3 className="h-6 w-6 text-[hsl(var(--info))]" />}
        />
        <StatCard
          title="Overall Completion"
          value={`${Math.round(summary.overall_completion_percent)}%`}
          icon={<TrendingUp className="h-6 w-6 text-[hsl(var(--accent))]" />}
        />
        <StatCard
          title="At Risk"
          value={summary.at_risk_key_results.length}
          icon={<AlertTriangle className="h-6 w-6 text-[hsl(var(--destructive))]" />}
          description={summary.at_risk_key_results.length > 0 ? "Needs attention" : "All on track"}
        />
      </div>

      {/* Overall Progress */}
      <div className="flex items-center gap-4 rounded-lg border bg-[hsl(var(--card))] p-4">
        <span className="text-sm font-medium">Overall Progress</span>
        <ProgressBar value={summary.overall_completion_percent} className="flex-1" />
        <span className="text-sm font-semibold">
          {Math.round(summary.overall_completion_percent)}%
        </span>
      </div>

      {/* Quarter Completion Chart */}
      <QuarterCompletionChart data={summary.completion_by_quarter} year={year} />

      {/* At-Risk Key Results */}
      <AtRiskList
        title="At-Risk Key Results"
        keyResults={summary.at_risk_key_results}
        emptyMessage="All your key results are on track."
      />
    </div>
  );
}
